/**
 * Face Detector — wrapper tipado sobre @mediapipe/face_detection
 *
 * Fail-closed (A): qualquer falha de inicialização, asset ou câmera
 * bloqueia validações e sinaliza erro com retry. Nunca cai silenciosamente
 * para polling antigo.
 */

import type { FaceBox } from "./stability-tracker";
import type { FaceDetection, Results } from "@mediapipe/face_detection";

export type DetectorStatus =
  | { type: "idle" }
  | { type: "loading" }
  | { type: "ready" }
  | { type: "error"; message: string; retryable: boolean };

export interface FaceDetectorOptions {
  model?: "short" | "full";
  minDetectionConfidence?: number;
  locateFile?: (file: string) => string;
}

interface Pending {
  id: number;
  resolve: (boxes: FaceBox[]) => void;
  reject: (err: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
}

export class FaceDetector {
  private instance: FaceDetection | null = null;
  private status: DetectorStatus = { type: "idle" };
  private model: "short" | "full";
  private minDetectionConfidence: number;
  private locateFile: (file: string) => string;
  private pending: Pending[] = [];
  private nextId = 0;

  constructor(options: FaceDetectorOptions = {}) {
    this.model = options.model ?? "short";
    this.minDetectionConfidence = options.minDetectionConfidence ?? 0.5;
    this.locateFile =
      options.locateFile ??
      ((file) => {
        // Fallback: assume assets copiados para /facial-assets/
        return `/facial-assets/${file}`;
      });
  }

  get currentStatus(): DetectorStatus {
    return this.status;
  }

  async initialize(): Promise<void> {
    if (this.status.type === "ready" || this.status.type === "loading") return;
    this.status = { type: "loading" };

    try {
      const { FaceDetection } = await import("@mediapipe/face_detection");
      const detector = new FaceDetection({
        locateFile: this.locateFile,
      });
      await detector.setOptions({
        model: this.model,
        minDetectionConfidence: this.minDetectionConfidence,
      });
      detector.onResults((results: Results) => {
        this.flushPending(results);
      });
      this.instance = detector;
      this.status = { type: "ready" };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.status = {
        type: "error",
        message: `Falha ao carregar detector facial: ${message}`,
        retryable: true,
      };
      throw new Error(this.status.message);
    }
  }

  async detect(video: HTMLVideoElement): Promise<FaceBox[]> {
    if (!this.instance || this.status.type !== "ready") {
      throw new Error("Detector não inicializado.");
    }
    const id = this.nextId++;
    return new Promise((resolve, reject) => {
      const timeout = globalThis.setTimeout(() => {
        this.removePending(id);
        reject(new Error("MediaPipe detection timeout"));
      }, 5000);

      this.pending.push({
        id,
        resolve,
        reject,
        timeout,
      });

      this.instance!.send({ image: video }).catch((error) => {
        this.removePending(id);
        globalThis.clearTimeout(timeout);
        const message = error instanceof Error ? error.message : String(error);
        this.status = {
          type: "error",
          message: `Falha na detecção: ${message}`,
          retryable: true,
        };
        reject(new Error(this.status.message));
      });
    });
  }

  async dispose(): Promise<void> {
    // Rejeitar pendentes para evitar promises penduradas
    const toReject = this.pending.splice(0, this.pending.length);
    for (const p of toReject) {
      globalThis.clearTimeout(p.timeout);
      p.reject(new Error("Detector disposed"));
    }
    if (this.instance) {
      await this.instance.close();
      this.instance = null;
    }
    this.status = { type: "idle" };
  }

  private flushPending(results: Results): void {
    const pending = this.pending.shift();
    if (!pending) return;
    globalThis.clearTimeout(pending.timeout);
    const boxes = (results.detections ?? []).map((d) => ({
      xCenter: d.boundingBox?.xCenter ?? 0,
      yCenter: d.boundingBox?.yCenter ?? 0,
      width: d.boundingBox?.width ?? 0,
      height: d.boundingBox?.height ?? 0,
    }));
    pending.resolve(boxes);
  }

  private removePending(id: number): void {
    const idx = this.pending.findIndex((p) => p.id === id);
    if (idx >= 0) {
      const p = this.pending.splice(idx, 1)[0];
      if (p) globalThis.clearTimeout(p.timeout);
    }
  }
}
